import { PropsWithChildren, useCallback, useEffect, useState } from 'react';

import fetchWithRetry from 'services/fetch-retry';

import { Dialog, Loader, useDialog } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useResponse } from 'contexts/response';
import './image.scss';

type ImageObject = { imageKey?: string; imageBase64?: string; [key: string]: any };

const ImageViewer = <T extends ImageObject>({
  children,
  images,
  getTitle
}: PropsWithChildren<{
  images: T[];
  getTitle?: (arg: T) => string;
}>) => {
  const { closeDialog, openDialog, ref } = useDialog();
  const { handleError } = useResponse();

  const [preview, setPreview] = useState<string | null>();
  const [index, setIndex] = useState<number>(-1);
  const [imageMap, setImageMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);

  const loadImage = useCallback(
    async ({ imageKey, imageBase64 }: ImageObject) => {
      if (imageBase64) {
        setPreview(imageBase64);
        openDialog();
        return;
      }
      if (!imageKey) {
        handleError('Image not found');
        return;
      }
      let base64: string = imageMap.get(imageKey) ?? '';
      if (base64) {
        setPreview(base64);
        openDialog();
        return;
      }
      setLoading(true);
      const response = await fetchWithRetry<string | null>(`/api/images/${imageKey}`, { method: 'GET' }, null);
      if (response) {
        base64 = response.replace(/\\r\\n/g, '');
        setImageMap(prevState => new Map(prevState).set(imageKey, base64)); /* cache image src */
        setPreview(base64);
        openDialog();
      } else {
        handleError('Could not load image');
      }
      setLoading(false);
    },
    [imageMap, handleError, openDialog, setImageMap, setPreview]
  );

  useEffect(() => {
    if (images && index in images && !!images[index]) loadImage(images[index]);
  }, [images, index, loadImage, openDialog]);

  if (!images?.length) return null;

  return (
    <Loader loading={loading}>
      <ErrorDialog />
      <button className="btn-back d-flex text-primary view-image" disabled={!images[0]} onClick={() => setIndex(0)}>
        <span className="material-icons">image</span>
        <span>{children ?? `VIEW IMAGES (${images.length})`}</span>
      </button>
      <Dialog
        ref={ref}
        closeDialog={() => {
          setIndex(-1);
          setPreview(null);
          closeDialog();
        }}
      >
        <div id="image-viewer">
          {preview && <img alt="preview" height="auto" width="100%" src={preview} />}
          {getTitle && <h4 className="p-1 text-white">{getTitle(images[index])}</h4>}
          <div className="btn-group grid-3">
            <button
              className="btn-back p-3"
              disabled={!index}
              onClick={() => setIndex(p => (p - 1 in images ? p - 1 : 0))}
            >
              <span className="material-icons text-white">arrow_back_ios</span>
            </button>
            <h5 className="align-self-center text-white">
              {index + 1} of {images.length}
            </h5>
            <button
              className="btn-back p-3"
              disabled={index >= images.length - 1}
              onClick={() => setIndex(p => (p + 1 in images ? p + 1 : 0))}
            >
              <span className="material-icons text-white">arrow_forward_ios</span>
            </button>
          </div>
        </div>
      </Dialog>
    </Loader>
  );
};

export default ImageViewer;
